import {
  CallbackProperty,
  Cartesian3,
  Color,
  ColorMaterialProperty,
  createGuid,
  createPropertyDescriptor,
  defined,
  PolylineArrowMaterialProperty,
  PolylineDashMaterialProperty,
  PolylineGlowMaterialProperty,
  PolylineOutlineMaterialProperty,
  Event,
  Matrix4,
  Transforms,
  HeadingPitchRoll,
  JulianDate,
  BoundingSphere,
  Quaternion,
} from "@cesium/engine";
import { Utils } from "@cesium/widgets";
import DitapViewer from "../basic/DitapViewer";
import EditMove from "./EditMove";

/**
 * 중심점 계산
 * @private
 * @param {Cartesian3[]} positions
 * @returns {Cartesian3}
 */
const getCenter = (positions) => {
  if (positions.length === 0) {
    return;
  }

  const minmax = Utils.computeMinMaxCartesian(positions);
  let center;
  if (minmax) {
    center = new Cartesian3(
      (minmax[0].x + minmax[1].x) / 2,
      (minmax[0].y + minmax[1].y) / 2,
      (minmax[0].z + minmax[1].z) / 2
    );
  }

  return center;
};

/**
 * Cartesian3 배열에 Matrix4 적용
 * @private
 * @param {Cartesian3[]} positions
 * @param {Matrix4} matrix
 * @param {Matrix4} inverse
 * @returns {Cartesian3[]}
 */
const setMatrixToPositionArray = (positions, matrix, inverse) => {
  const newPositions = [];

  // 회전행렬 적용
  for (const position of positions) {
    const newPosition = position.clone(new Cartesian3());

    // 역행렬
    if (defined(inverse)) {
      Matrix4.multiplyByPoint(inverse, newPosition, newPosition);
    }

    // 회전행렬
    Matrix4.multiplyByPoint(matrix, newPosition, newPosition);

    newPositions.push(newPosition);
  }

  return newPositions;
};

/**
 * Geometry Transform
 * @private
 * @param {Cartesian3[]} positions 좌표배열
 * @param {Cartesian3} origin 원본점
 * @param {Cartesian3} newPosition 이동점
 * @param {Quaternion} oldOrientaion 회전원본
 * @param {Quaternion} newOrientaion 적용회전
 * @param {Cartesian3} oldScale 비율원본
 * @param {Cartesian3} newScale 적용비율
 * @returns {Cartesian3[]} 변환좌표배열
 */
const transformGeometry = (
  positions,
  origin,
  newPosition,
  oldOrientaion,
  newOrientaion,
  oldScale,
  newScale
) => {
  // wall 변환행렬
  const matrix = Matrix4.fromTranslationQuaternionRotationScale(
    newPosition,
    newOrientaion,
    newScale,
    new Matrix4()
  );

  // wall 역행렬
  const inverse = Matrix4.inverse(
    Matrix4.fromTranslationQuaternionRotationScale(
      origin,
      oldOrientaion,
      oldScale,
      new Matrix4()
    ),
    new Matrix4()
  );

  const newPositions = setMatrixToPositionArray(positions, matrix, inverse);

  return newPositions;
};

/**
 * position 변경 핸들러
 * @param {object} parent
 * @param {*} newValue
 * @param {*} oldValue
 */
const positionChangedHandler = (parent) => {
  const pointEntities = parent.pointEntities;
  const polylineEntity = parent.polylineEntity;

  // polyline positions
  const positions = Utils.getPropertyValue(parent.positions);
  const origin = getCenter(positions);
  const position = Utils.getPropertyValue(parent.position);
  const orientationOffset = Transforms.headingPitchRollQuaternion(
    origin,
    new HeadingPitchRoll(0, 0, 0)
  );
  const orientation =
    Utils.getPropertyValue(parent.orientation) ||
    Transforms.headingPitchRollQuaternion(
      position,
      new HeadingPitchRoll(0, 0, 0)
    );
  const scaleOffset = Cartesian3.ONE;
  const scale = Utils.getPropertyValue(parent.scale) || Cartesian3.ONE;

  // CallbackProperty
  if (
    parent.position instanceof CallbackProperty ||
    parent.orientation instanceof CallbackProperty ||
    parent.scale instanceof CallbackProperty
  ) {
    // Point
    for (let i = 0; i < pointEntities.length; i++) {
      const pointPosition = positions[i];
      EditMove.setPosition(
        pointEntities[i],
        new CallbackProperty((time) => {
          const newPosition = Utils.getPropertyValue(parent.position, time);
          const newOrientation =
            Utils.getPropertyValue(parent.orientation, time) ||
            Transforms.headingPitchRollQuaternion(
              newPosition,
              new HeadingPitchRoll(0, 0, 0)
            );
          const newScale =
            Utils.getPropertyValue(parent.scale, time) || Cartesian3.ONE;
          const newPositions = transformGeometry(
            [pointPosition],
            origin,
            newPosition,
            orientationOffset,
            newOrientation,
            scaleOffset,
            newScale
          );

          return newPositions[0];
        }, false)
      );
    }

    // Polyline
    polylineEntity.polyline.positions = new CallbackProperty((time) => {
      const newPosition = Utils.getPropertyValue(parent.position, time);
      const newOrientation =
        Utils.getPropertyValue(parent.orientation, time) ||
        Transforms.headingPitchRollQuaternion(
          newPosition,
          new HeadingPitchRoll(0, 0, 0)
        );
      const newScale =
        Utils.getPropertyValue(parent.scale, time) || Cartesian3.ONE;
      const newPositions = transformGeometry(
        positions,
        origin,
        newPosition,
        orientationOffset,
        newOrientation,
        scaleOffset,
        newScale
      );

      return newPositions;
    }, false);

    // ConstantProperty
  } else {
    const newPolylinePositions = transformGeometry(
      positions,
      origin,
      position,
      orientationOffset,
      orientation,
      scaleOffset,
      scale
    );

    // Point
    for (let i = 0; i < pointEntities.length; i++) {
      const pointPosition = newPolylinePositions[i];
      pointEntities[i].position = pointPosition;
    }

    // Polyline
    polylineEntity.polyline.positions = newPolylinePositions;
  }
};

/**
 * positions 변경 핸들러
 * @param {object} parent
 * @param {*} newValue
 * @param {*} oldValue
 */
const positionsChangedHandler = (parent, positions) => {
  if (defined(parent.polylineEntity)) {
    // 초기화
    parent.reset();

    parent._positions = positions;

    // Entity 생성
    parent.createEntity();
  }
};

/**
 * orientation 변경 핸들러
 * @param {object} parent
 * @param {*} newValue
 * @param {*} oldValue
 */
const orientationChangedHandler = (parent) => {
  const pointEntities = parent.pointEntities;
  const polylineEntity = parent.polylineEntity;

  const positions = Utils.getPropertyValue(parent.positions);
  const origin = getCenter(positions);
  const position = Utils.getPropertyValue(parent.position);
  const orientationOffset = Transforms.headingPitchRollQuaternion(
    origin,
    new HeadingPitchRoll(0, 0, 0)
  );
  const orientation =
    Utils.getPropertyValue(parent.orientation) ||
    Transforms.headingPitchRollQuaternion(
      position,
      new HeadingPitchRoll(0, 0, 0)
    );
  const scaleOffset = Cartesian3.ONE;
  const scale = Utils.getPropertyValue(parent.scale) || Cartesian3.ONE;

  // CallbackProperty
  if (
    parent.position instanceof CallbackProperty ||
    parent.orientation instanceof CallbackProperty ||
    parent.scale instanceof CallbackProperty
  ) {
    // Point
    for (let i = 0; i < pointEntities.length; i++) {
      const pointPosition = positions[i];
      EditMove.setPosition(
        pointEntities[i],
        new CallbackProperty((time) => {
          const newPosition = Utils.getPropertyValue(parent.position, time);
          const newOrientation =
            Utils.getPropertyValue(parent.orientation, time) ||
            Transforms.headingPitchRollQuaternion(
              newPosition,
              new HeadingPitchRoll(0, 0, 0)
            );
          const newScale =
            Utils.getPropertyValue(parent.scale, time) || Cartesian3.ONE;
          const newPositions = transformGeometry(
            [pointPosition],
            origin,
            newPosition,
            orientationOffset,
            newOrientation,
            scaleOffset,
            newScale
          );

          return newPositions[0];
        }, false)
      );
    }

    // Polyline
    polylineEntity.polyline.positions = new CallbackProperty((time) => {
      const newPosition = Utils.getPropertyValue(parent.position, time);
      const newOrientation =
        Utils.getPropertyValue(parent.orientation, time) ||
        Transforms.headingPitchRollQuaternion(
          newPosition,
          new HeadingPitchRoll(0, 0, 0)
        );
      const newScale =
        Utils.getPropertyValue(parent.scale, time) || Cartesian3.ONE;
      const newPositions = transformGeometry(
        positions,
        origin,
        newPosition,
        orientationOffset,
        newOrientation,
        scaleOffset,
        newScale
      );

      return newPositions;
    }, false);

    // ConstantProperty
  } else {
    const newPolylinePositions = transformGeometry(
      positions,
      origin,
      position,
      orientationOffset,
      orientation,
      scaleOffset,
      scale
    );

    // Point
    for (let i = 0; i < pointEntities.length; i++) {
      const pointPosition = newPolylinePositions[i];
      pointEntities[i].position = pointPosition;
    }

    // Polyline
    polylineEntity.polyline.positions = newPolylinePositions;
  }
};

/**
 * scale 변경 핸들러
 * @param {object} parent
 * @param {*} newValue
 * @param {*} oldValue
 */
const scaleChangedHandler = (parent) => {
  const pointEntities = parent.pointEntities;
  const polylineEntity = parent.polylineEntity;

  const positions = Utils.getPropertyValue(parent.positions);
  const origin = getCenter(positions);
  const position = Utils.getPropertyValue(parent.position);
  const orientationOffset = Transforms.headingPitchRollQuaternion(
    origin,
    new HeadingPitchRoll(0, 0, 0)
  );
  const orientation =
    Utils.getPropertyValue(parent.orientation) ||
    Transforms.headingPitchRollQuaternion(
      position,
      new HeadingPitchRoll(0, 0, 0)
    );
  const scaleOffset = Cartesian3.ONE;
  const scale = Utils.getPropertyValue(parent.scale) || Cartesian3.ONE;

  // CallbackProperty
  if (
    parent.position instanceof CallbackProperty ||
    parent.orientation instanceof CallbackProperty ||
    parent.scale instanceof CallbackProperty
  ) {
    // Point
    for (let i = 0; i < pointEntities.length; i++) {
      const pointPosition = positions[i];
      EditMove.setPosition(
        pointEntities[i],
        new CallbackProperty((time) => {
          const newPosition = Utils.getPropertyValue(parent.position, time);
          const newOrientation =
            Utils.getPropertyValue(parent.orientation, time) ||
            Transforms.headingPitchRollQuaternion(
              newPosition,
              new HeadingPitchRoll(0, 0, 0)
            );
          const newScale =
            Utils.getPropertyValue(parent.scale, time) || Cartesian3.ONE;
          const newPositions = transformGeometry(
            [pointPosition],
            origin,
            newPosition,
            orientationOffset,
            newOrientation,
            scaleOffset,
            newScale
          );

          return newPositions[0];
        }, false)
      );
    }

    // Polyline
    polylineEntity.polyline.positions = new CallbackProperty((time) => {
      const newPosition = Utils.getPropertyValue(parent.position, time);
      const newOrientation =
        Utils.getPropertyValue(parent.orientation, time) ||
        Transforms.headingPitchRollQuaternion(
          newPosition,
          new HeadingPitchRoll(0, 0, 0)
        );
      const newScale =
        Utils.getPropertyValue(parent.scale, time) || Cartesian3.ONE;
      const newPositions = transformGeometry(
        positions,
        origin,
        newPosition,
        orientationOffset,
        newOrientation,
        scaleOffset,
        newScale
      );

      return newPositions;
    }, false);

    // ConstantProperty
  } else {
    const newPolylinePositions = transformGeometry(
      positions,
      origin,
      position,
      orientationOffset,
      orientation,
      scaleOffset,
      scale
    );

    // Point
    for (let i = 0; i < pointEntities.length; i++) {
      const pointPosition = newPolylinePositions[i];
      pointEntities[i].position = pointPosition;
    }

    // Polyline
    polylineEntity.polyline.positions = newPolylinePositions;
  }
};

/**
 * DefinitionChanged 이벤트 핸들러
 * @param {object} parent 부모 객체
 * @param {string} property 프로퍼티명
 * @param {*} newValue 변경값
 * @param {*} oldValue 이전값
 */
const definitionChangedHandler = (parent, property, newValue, oldValue) => {
  if (property === "position") {
    positionChangedHandler(parent);
  } else if (property === "positions") {
    positionsChangedHandler(parent, newValue);
  } else if (property === "orientation") {
    orientationChangedHandler(parent);
  } else if (property === "scale") {
    scaleChangedHandler(parent);
  }
};

/**
 * 선 엔티티 표시 클래스.</br>
 * Linestring entity display class.
 *
 * @alias LinestringEntity
 * @constructor
 *
 * @param {DitapViewer} viewer 사용할 Ditap Viewer 인스턴스.</br> The DitapViewer instance to use.
 * @param {Cartesian3[]} [position] 선 생성을 위한 Cartesian 좌표.</br> Cartesian coordinates for line creation.
 * @example
 *
 * // 샌드캐슬 예제 'DiTAP - Linestring Entity' 참조
 *
 * const viewer = new DitapViewer("ditapContainer");
 *
 * // 1. 위치 지정 없이 객체 생성. (InteractionLinestringEntity를 통해 선 엔티티 표시.)
 * const LinestringEntity = new Ditap.LinestringEntity(viewer);
 *
 * const InteractionLinestringEntity = new Ditap.InteractionLinestringEntity();
 * InteractionLinestringEntity.LinestringEntity = LinestringEntity;
 *
 * // 2. 위치 지정하여 인스턴스 생성. (InteractionLinestringEntity 통한 수정 불가.)
 * const position1 = new Ditap.Cartesian3.fromDegrees(
 *  126.97759025002786,
 *  37.56679755138474
 * );
 * const position2 = new Ditap.Cartesian3.fromDegrees(
 *  126.97764810657144,
 *  37.566518888624664
 * );
 *
 * const LinestringEntity = new Ditap.LinestringEntity(viewer, [position1, position2]);
 */
class LinestringEntity {
  constructor(viewer, positions) {
    this._id = `LinestringEntity${createGuid()}`;
    this._viewer = undefined;
    if (viewer instanceof DitapViewer) {
      this.viewer = viewer;
      viewer.ditapStorage.linestringEntity[this.id] = this;
    } else {
      console.error("Type doesn't match");
    }

    this._pointEntities = [];
    this._polylineEntity = undefined;
    this._entityNum = 0;
    this._displayCondition = 2000;
    this._depthFailAlpha = 0.3;
    // if (positions) {
    //   this._positions = positions;
    //   this.createEntity();
    // } else {
    //   this._positions = [];
    // }

    this._position = undefined;
    this._positions = [];
    this._orientation = undefined;
    this._scale = undefined;

    // PointEntity Property 값 변경 감지 이벤트
    this._definitionChanged = new Event();

    // position, scale Property 정의
    Object.defineProperties(this, {
      /**
       * 선 엔티티 중심 좌표. </br>
       * LineEntity center coordinates.
       * @type {PositionProperty|undefined}
       */
      position: Utils.createPositionPropertyDescriptor("position"),

      /**
       * 선 엔티티 좌표 목록. </br>
       * LineEntity coordinate list.
       * @type {Property|Cartesian3[]}
       */
      positions: createPropertyDescriptor("positions"),

      /**
       * 선 엔티티 방향. </br>
       * LineEntity orientation.
       * @type {Property|undefined}
       */
      orientation: createPropertyDescriptor("orientation"),

      /**
       * 선 엔티티 비율. </br>
       * LineEntity Scale.
       * @type {PositionProperty|undefined}
       */
      scale: Utils.createPositionPropertyDescriptor("scale"),
    });

    // position, scale 값 변경 이벤트 핸들러 추가
    this._definitionChanged.addEventListener(definitionChangedHandler);

    // position 초기화
    if (positions) {
      this.positions = positions;
      this.createEntity();
    }
  }

  /**
   * 사용할 Ditap Viewer 인스턴스.</br>
   * The DitapViewer instance to use.
   * @type {DitapViewer}
   */
  set viewer(viewer) {
    if (viewer instanceof DitapViewer) {
      this._viewer = viewer;
    }
  }
  get viewer() {
    return this._viewer;
  }

  /*
   * 선 엔티티 ID.</br>
   * ID to assign to the LineEntity.
   * @type {string}
   */
  set id(id) {
    this._id = id;
  }
  get id() {
    return this._id;
  }

  /*
   * 엔티티 번호. </br>
   * Number of entities.
   * @type {number}
   */
  set entityNum(entityNum) {
    this._entityNum = entityNum;
  }
  get entityNum() {
    return this._entityNum;
  }

  /**
   * 선 생성 할 Cartesian 배열</br>
   * Cartesian array for create LineEntity.
   * @type {Cartesian3[]}
   * @readonly
   */
  set positions(positions) {
    this._positions = positions;
  }
  get positions() {
    return this._positions;
  }

  /**
   * 선 생성으로 얻은 포인트 엔티티 배열.</br>
   * Array of point entities obtained by line creation.
   * @type {Entity[]}
   * @readonly
   */
  set pointEntities(pointEntities) {
    this._pointEntities = pointEntities;
  }
  get pointEntities() {
    return this._pointEntities;
  }

  /**
   * 선 생성으로 얻은 polyline Entity.</br>
   * Polyline entities obtained by line creation.
   * @type {Entity}
   * @readonly
   */
  set polylineEntity(polylineEntity) {
    this._polylineEntity = polylineEntity;
  }
  get polylineEntity() {
    return this._polylineEntity;
  }

  /*
   * 엔티티의 DistanceDisplayCondition 값.</br>
   * DistanceDisplayCondition value of entity.
   * @type {number}
   */
  set displayCondition(displayCondition) {
    this._displayCondition = displayCondition;
  }
  get displayCondition() {
    return this._displayCondition;
  }

  /*
   * 엔티티의 depthFailAlpha 값.</br>
   * depthFailAlpha value of entity.
   * @type {number}
   */
  set depthFailAlpha(depthFailAlpha) {
    this._depthFailAlpha = depthFailAlpha;
  }
  get depthFailAlpha() {
    return this._depthFailAlpha;
  }

  /**
   * 인스턴스 설정 값 초기화. </br>
   * Initializing instance settings.
   */
  reset() {
    const viewer = this.viewer;
    for (const entity of this.pointEntities) {
      viewer.entities.remove(entity);
    }
    viewer.entities.remove(this.polylineEntity);

    this._pointEntities = [];
    this._polylineEntity = undefined;
    this._entityNum = 0;
    this._displayCondition = 2000;
    this._depthFailAlpha = 0.3;
    this._positions = [];
    this._position = undefined;
    this._orientation = undefined;
    this._scale = undefined;
  }

  /**
   * 인스턴스 파괴. 영구적으로 제거하는 경우 호출.</br>
   * Destroys the instance. Should be called if permanently removing the instance from layout.
   */
  destroy() {
    this.reset();
    this.viewer.ditapStorage.removeInstanceById(this.id);
  }

  /*
   * 배열의 마지막 요소 제거. </br>
   * Remove last element from array.
   * @param {Entity[]} array 요소를 제거할 배열.</br> Array to remove last element.
   */
  removeLastElement(array) {
    array.pop();
  }

  /*
   * Ditap Viewer에서 엔티티 제거.</br>
   * Remove entity from viewer.
   * @param {Entity} entity 제거할 엔티티.</br> Entities to remove.
   */
  removeViewerEntity(entity) {
    this.viewer.entities.remove(entity);
  }

  /*
   * 엔티티 색상 속성 변경.</br>
   * Change the entity color property.
   * @param {Entity} entity 색상을 변경할 엔티티.</br> The Entity to Change Color.
   * @param {Color} color 변경할 색상.</br> The Color to change.
   */
  changeColor(entity, color) {
    if (entity._polyline) {
      entity.polyline.material = color;
    }
    if (entity._point) {
      entity.point.color = color;
    }
  }

  /*
   * 엔티티 너비 속성 변경.</br>
   * Change the entity width property.
   * @param {Entity} entity 너비를 변경할 엔티티.</br> The Entity to Change width.
   * @param {number} width 변경할 너비 값.</br> The Width to change.
   */
  changeWidth(entity, width) {
    entity.polyline.width = width;
  }

  /*
   * 엔티티 픽셀 사이즈 변경.</br>
   * Change the entity pixel size property.
   * @param {Entity} entity 픽셀 사이즈를 변경할 엔티티.</br> The Entity to Change width.
   * @param {number} pixelSize 픽셀 값.</br> The pixel size to change.
   */
  changePointPixelSize(entity, pixelSize) {
    entity.point.pixelSize = pixelSize;
  }

  /*
   * Ditap Viewer에 선 엔티티 생성.</br>
   * Create LinestringEntity in the DitapViewer.
   */
  createEntity() {
    const positions = Utils.getPropertyValue(this.positions);
    const displayCondition = Utils.getDistanceDisplayConditon(positions);

    if (displayCondition > 2000) {
      this.displayCondition = displayCondition;
    }

    // Point
    for (let i = 0; i < positions.length; i++) {
      // callback or constant 좌표값 생성
      const position =
        positions instanceof CallbackProperty
          ? new CallbackProperty(() => positions[i], false)
          : positions[i];

      // 포인트 엔티티 생성
      const linePoint = Utils.createPoint({
        viewer: this.viewer,
        id: this.id + this.entityNum,
        position: position,
        outlineColor: Color.fromBytes(178, 8, 228, 255),
        displayCondition: this.displayCondition,
      });
      this.pointEntities.push(linePoint);
      this.entityNum++;
    }

    // positions.forEach((position) => {
    //   const linePoint = Utils.createPoint({
    //     viewer: this.viewer,
    //     id: this.id + this.entityNum,
    //     position: position,
    //     outlineColor: Color.fromBytes(178, 8, 228, 255),
    //     displayCondition: this.displayCondition,
    //   });
    //   this.pointEntities.push(linePoint);
    //   this.entityNum++;
    // });

    // Polyline
    const linePolyline = Utils.createPolyline({
      viewer: this.viewer,
      id: this.id + this.entityNum,
      positions: positions,
      material: Color.fromBytes(178, 8, 228, 255),
      displayCondition: this.displayCondition,
    });
    this.entityNum++;
    this.polylineEntity = linePolyline;

    // LinestringEntity position 설정
    this._position = linePolyline.position;
  }

  /*
   * 모든 선 엔티티 표시 여부 변경.</br>
   * Sets all entities in LinestringEntity shold be displayed.
   * @param {boolean} flag 표시 여부 값.</br> The value of show.
   */
  setAllEntitiesShow(flag) {
    this.setAllPointEntitiesShow(flag);
    this.polylineEntity.polyline.show = flag;
  }

  /*
   * 모든 점 엔티티 표시 여부 변경.</br>
   * Sets all entities in Point Entity shold be displayed.
   * @param {boolean} flag 표시 여부 값.</br> The value of show.
   */
  setAllPointEntitiesShow(flag) {
    this.pointEntities.forEach((point) => {
      point.point.show = flag;
    });
  }

  /*
   * 선 엔티티 재질 변경.</br>
   * Set the material of polylineEntity.
   * @param {MaterialProperty} material 변경할 재질.</br> The material to change.
   */
  setPolylineMaterial(material) {
    this.polylineEntity.polyline.material = material;
  }

  /*
   * 선 엔티티 depthFailMaterial 변경.</br>
   * Set the depthFailMaterial of polylineEntity.
   * @param {MaterialProperty} depthFailMaterial 변경할 depthFailMateria.</br> The depthFailMaterial to change.
   */
  setPolylineDepthFailMaterial(depthFailMaterial) {
    this.polylineEntity.polyline.depthFailMaterial = depthFailMaterial;
  }

  /*
   * 선 엔티티 너비 변경.</br>
   * Set the width of polylineEntity.
   * @param {number} width 변경할 너비 값.</br> The width to change.
   */
  setPolylineWidth(width) {
    this.polylineEntity.polyline.width = Number(width);
  }

  /*
   * 선 엔티티 색상 변경.</br>
   * Set the color of pointEntity.
   * @param {Entity} pointEntity 색상을 변경할 엔티티.</br> The Entity to change color.
   * @param {Color} color 변경할 색상.</br> The color to change.
   */
  setPointOutlineColor(pointEntity, color) {
    pointEntity.point.outlineColor = color;
  }

  /*
   * 점 엔티티 아웃라인 색상 변경.</br>
   * Sets all color of pointEntities in LinestringEntity.
   * @param {Color} color 변경할 색상.</br> The color to change.
   */
  setAllPointsOutlineColor(color) {
    this.pointEntities.forEach((pointEntity) => {
      this.setPointOutlineColor(pointEntity, color);
    });
  }

  /*
   * 모든 포인트 엔티티 픽셀 사이즈 변경.</br>
   * Sets all pixel size of pointEntities in LinestringEntity.
   * @param {number} pixelSize 변경할 픽셀 사이즈.</br> The pixel size to change.
   */
  setAllPointsPixelSize(pixelSize) {
    this.pointEntities.forEach((pointEntity) => {
      this.setPointPixelSize(pointEntity, pixelSize);
    });
  }

  /*
   * 포인트 엔티티 픽셀 사이즈 변경.</br>
   * Set pixel size of pointEntitiy.
   * @param {Entity} pointEntity 픽셀 사이즈 변경할 포인트 엔티티.</br> The pointEntity to change pixelSize.
   * @param {number} size 변경할 픽셀 사이즈.</br> The pixel size to change.
   */
  setPointPixelSize(pointEntity, size) {
    pointEntity.point.pixelSize = size;
  }

  /*
   * 선 엔티티의 materialStyle 이름 조회.</br>
   * Get materialStyle name of linestringEntity to string.
   * @param {LinestringEntity} linestringEntity 조회할 선 엔티티.</br> The instance of LinestrintEntity.
   * @return {String} 조회된 materialStyle 이름.</br> materialStyle string name.
   */
  getMaterialStyle() {
    const polyline = this.polylineEntity.polyline;

    if (polyline) {
      let style;
      if (polyline.material instanceof ColorMaterialProperty) {
        style = "spline";
      } else if (polyline.material instanceof PolylineDashMaterialProperty) {
        style = "dash";
      } else if (polyline.material instanceof PolylineGlowMaterialProperty) {
        style = "glow";
      } else if (polyline.material instanceof PolylineOutlineMaterialProperty) {
        style = "outline";
      } else if (polyline.material instanceof PolylineArrowMaterialProperty) {
        style = "arow";
      }
      return style;
    }
  }

  /**
   * 지정된 시간에 엔티티에 대한 positions 계산. <br>
   * Calculate positions for an entity at a given time.
   * @param {JulianDate} [time=JulianDate] time 모델 positions를 검색하는 시간. <br> The time to retrieve model positions for.
   * @returns {positions | undefined} Model positions. <br> Model positions.
   */
  computePositions(time = new JulianDate()) {
    const position = Utils.getPropertyValue(this.position, time);
    const positions = Utils.getPropertyValue(this.positions, time);
    if (!(position && positions && 0 < positions.length)) {
      return;
    }

    const center = getCenter(positions);
    const matrix = this.computeModelMatrix();

    if (matrix) {
      const inverse = Matrix4.inverse(
        Matrix4.fromTranslationQuaternionRotationScale(
          center,
          Quaternion.IDENTITY,
          Cartesian3.ONE
        ),
        new Matrix4()
      );

      const newPositions = setMatrixToPositionArray(positions, matrix, inverse);
      return newPositions;
    }
  }

  /**
   * 지정된 시간에 엔티티에 대한 BoundingSphere 계산. <br>
   * Calculate BoundingSphere for an entity at a given time.
   * @param {JulianDate} [time=JulianDate] time 모델 BoundingSphere를 검색하는 시간. <br> The time to retrieve model BoundingSphere for.
   * @returns {BoundingSphere | undefined} Model BoundingSphere. <br> Model BoundingSphere.
   */
  computeBoundingSphere(time = new JulianDate()) {
    const position = Utils.getPropertyValue(this.position, time);
    const positions = Utils.getPropertyValue(this.positions, time);
    if (!(position && positions && 0 < positions.length)) {
      return;
    }

    let center = getCenter(positions);
    let minmax = Utils.computeMinMaxCartesian(positions);

    const matrix = this.computeModelMatrix();
    if (matrix) {
      const inverse = Matrix4.inverse(
        Matrix4.fromTranslationQuaternionRotationScale(
          center,
          Quaternion.IDENTITY,
          Cartesian3.ONE
        ),
        new Matrix4()
      );

      const newPositions = setMatrixToPositionArray(positions, matrix, inverse);

      center = getCenter(newPositions);
      minmax = Utils.computeMinMaxCartesian(newPositions);
    }

    // BoundingSphere 반지름
    const radius = Math.sqrt(
      (Math.pow(minmax[1].x - minmax[0].x, 2) +
        Math.pow(minmax[1].y - minmax[0].y, 2) +
        Math.pow(minmax[1].z - minmax[0].z, 2)) /
        2
    );

    const boundingSphere = new BoundingSphere(center, radius);

    return boundingSphere;
  }

  /**
   * 지정된 시간에 엔티티 변환에 대한 모델 행렬 계산. <br>
   * Compute model matrix for entity transformation at specified time.
   * @param {JulianDate} [time=JulianDate] time 모델 행렬을 검색하는 시간. <br> The time to retrieve model matrix for.
   * @returns {Matrix4 | undefined} 모델 행렬. <br> Model matrix.
   */
  computeModelMatrix(time = new JulianDate()) {
    const position = Utils.getPropertyValue(this.position, time);
    if (!defined(position)) {
      return;
    }

    const orientation =
      Utils.getPropertyValue(this.orientation, time) ||
      Transforms.headingPitchRollQuaternion(
        position,
        new HeadingPitchRoll(0, 0, 0)
      );
    const scale = Utils.getPropertyValue(this.scale, time) || Cartesian3.ONE;

    const matrix = Matrix4.fromTranslationQuaternionRotationScale(
      position,
      orientation,
      scale,
      new Matrix4()
    );

    return matrix;
  }
}
export default LinestringEntity;
